/**
 * ADMIN DAILY CLOSING REPORTS SERVICE
 * Comprehensive staff monitoring and daily closing reports for Admin Web App
 * Supports monitoring each staff user's sales reports and end-of-day summaries
 */

export interface AdminDailyClosingReport {
  id?: string;
  report_date: string;
  staff_member: {
    id: string;
    name: string;
    email: string;
    role: string;
    branch: string;
  };
  shift_info: {
    start_time: string;
    end_time: string;
    duration_hours: number;
    break_time_minutes: number;
  };
  
  // Financial Performance
  financial_performance: {
    total_sales: number;
    total_orders: number;
    total_customers: number;
    average_order_value: number;
    gross_revenue: number;
    net_revenue: number;
    tax_collected: number;
    discounts_given: number;
    refunds_issued: number;
    sales_per_hour: number;
    orders_per_hour: number;
  };
  
  // Payment Methods Performance
  payment_performance: {
    cash: { count: number; amount: number; percentage: number };
    card: { count: number; amount: number; percentage: number };
    paypal: { count: number; amount: number; percentage: number };
    other: { count: number; amount: number; percentage: number };
  };
  
  // Cash Management
  cash_management: {
    opening_cash: number;
    cash_sales: number;
    cash_refunds: number;
    expected_cash: number;
    actual_cash: number;
    cash_difference: number;
    cash_over_short: number;
    cash_accuracy_percentage: number;
  };
  
  // Sales Analysis
  sales_analysis: {
    hourly_breakdown: Array<{
      hour: number;
      orders: number;
      revenue: number;
      average_order_value: number;
    }>;
    top_selling_items: Array<{
      name: string;
      quantity: number;
      revenue: number;
      percentage_of_sales: number;
    }>;
    category_performance: Array<{
      category: string;
      orders: number;
      revenue: number;
      percentage_of_sales: number;
    }>;
  };
  
  // Performance Metrics
  performance_metrics: {
    peak_performance_hour: string;
    slowest_hour: string;
    average_service_time_minutes: number;
    customer_satisfaction_score: number;
    error_rate_percentage: number;
    efficiency_score: number;
  };
  
  // Comparison with Team
  team_comparison: {
    staff_rank: number;
    total_staff_count: number;
    above_average_performance: boolean;
    team_average_sales: number;
    team_average_orders: number;
    performance_vs_team_percentage: number;
  };
  
  // Audit Trail
  audit_trail: {
    report_generated_at: string;
    generated_by: string;
    last_order_time: string;
    first_order_time: string;
    system_uptime_hours: number;
    errors_logged: number;
    manual_adjustments: number;
  };
  
  // Status
  status: 'draft' | 'final' | 'approved' | 'disputed' | 'archived';
  created_at: string;
  updated_at: string;
}

export interface AdminInterimSalesSummary {
  id?: string;
  summary_time: string;
  staff_member: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  period_start: string;
  period_end: string;
  
  // Current Performance
  current_performance: {
    current_sales: number;
    current_orders: number;
    hourly_rate: number;
    projected_daily_sales: number;
    projected_daily_orders: number;
    time_to_goal: string;
    goal_completion_percentage: number;
  };
  
  // Recent Activity
  recent_activity: {
    last_hour_orders: number;
    last_hour_revenue: number;
    last_hour_customers: number;
    peak_performance_hour: string;
    current_trend: 'increasing' | 'decreasing' | 'stable';
    trend_percentage: number;
  };
  
  // Performance Alerts
  performance_alerts: Array<{
    type: 'warning' | 'info' | 'success' | 'error' | 'achievement';
    message: string;
    timestamp: string;
    severity: 'low' | 'medium' | 'high';
  }>;
  
  // Staff Comparison
  staff_comparison: {
    rank_among_staff: number;
    total_active_staff: number;
    performance_vs_peers: number;
    best_performing_staff: string;
    areas_for_improvement: string[];
  };
  
  created_at: string;
}

export interface StaffPerformanceSummary {
  staff_id: string;
  staff_name: string;
  staff_email: string;
  staff_role: string;
  total_reports: number;
  average_daily_sales: number;
  average_daily_orders: number;
  best_performance_date: string;
  worst_performance_date: string;
  consistency_score: number;
  improvement_trend: 'improving' | 'declining' | 'stable';
  last_report_date: string;
  status: 'active' | 'inactive' | 'on_break';
}

class AdminDailyClosingReportsService {
  private static instance: AdminDailyClosingReportsService;

  private constructor() {
    console.log('📊 Admin Daily Closing Reports Service initialized');
  }

  public static getInstance(): AdminDailyClosingReportsService {
    if (!AdminDailyClosingReportsService.instance) {
      AdminDailyClosingReportsService.instance = new AdminDailyClosingReportsService();
    }
    return AdminDailyClosingReportsService.instance;
  }

  /**
   * Generate comprehensive daily closing report for specific staff member
   */
  public async generateStaffDailyClosingReport(
    staffId: string,
    reportDate: string,
    openingCash: number = 0,
    actualCash: number = 0
  ): Promise<AdminDailyClosingReport> {
    try {
      console.log(`📊 [ADMIN DAILY CLOSING] Generating daily closing report for staff ${staffId} on ${reportDate}...`);
      
      // Get staff information (staffId can be email or name)
      const staffInfo = await this.getStaffInfo(staffId);
      
      // Calculate shift times
      const shiftStart = new Date(reportDate);
      shiftStart.setHours(9, 0, 0, 0); // 9:00 AM
      
      const shiftEnd = new Date(reportDate);
      shiftEnd.setHours(23, 59, 59, 999); // 11:59 PM
      
      // Fetch all data for the day for this specific staff member (use email)
      const [orders, payments] = await Promise.all([
        this.getStaffOrdersForDate(staffInfo.email, reportDate),
        this.getStaffPaymentsForDate(staffInfo.email, reportDate)
      ]);

      // Generate comprehensive report
      const report: Omit<AdminDailyClosingReport, 'id'> = {
        report_date: reportDate,
        staff_member: staffInfo,
        shift_info: this.generateShiftInfo(shiftStart, shiftEnd),
        
        financial_performance: this.generateFinancialPerformance(orders, payments, shiftStart, shiftEnd),
        payment_performance: this.generatePaymentPerformance(payments),
        cash_management: this.generateCashManagement(orders, payments, openingCash, actualCash),
        sales_analysis: this.generateSalesAnalysis(orders),
        performance_metrics: this.generatePerformanceMetrics(orders),
        team_comparison: await this.generateTeamComparison(staffInfo.email, reportDate, orders, payments),
        audit_trail: this.generateAuditTrail(orders),
        
        status: 'draft',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Save report to Firebase
      const savedReport = await this.saveAdminDailyClosingReport(report);
      
      console.log(`✅ [ADMIN DAILY CLOSING] Daily closing report generated successfully for staff ${staffId}`);
      return savedReport;

    } catch (error) {
      console.error(`❌ [ADMIN DAILY CLOSING] Failed to generate daily closing report for staff ${staffId}:`, error);
      throw error;
    }
  }

  /**
   * Generate interim sales summary for specific staff member
   */
  public async generateStaffInterimSalesSummary(
    staffId: string,
    periodHours: number = 1
  ): Promise<AdminInterimSalesSummary> {
    try {
      console.log(`📊 [ADMIN INTERIM SALES] Generating interim sales summary for staff ${staffId}...`);
      
      const now = new Date();
      const periodStart = new Date(now.getTime() - (periodHours * 60 * 60 * 1000));
      
      // Get staff information (staffId can be email or name)
      const staffInfo = await this.getStaffInfo(staffId);
      
      // Fetch recent data for this staff member (use email)
      const [orders, payments] = await Promise.all([
        this.getStaffOrdersForPeriod(staffInfo.email, periodStart.toISOString(), now.toISOString()),
        this.getStaffPaymentsForPeriod(staffInfo.email, periodStart.toISOString(), now.toISOString())
      ]);

      const summary: Omit<AdminInterimSalesSummary, 'id'> = {
        summary_time: now.toISOString(),
        staff_member: staffInfo,
        period_start: periodStart.toISOString(),
        period_end: now.toISOString(),
        
        current_performance: this.generateCurrentPerformance(orders, payments, periodHours),
        recent_activity: this.generateRecentActivity(orders, payments),
        performance_alerts: this.generatePerformanceAlerts(orders, payments),
        staff_comparison: await this.generateStaffComparison(staffInfo.email, orders, payments),
        
        created_at: now.toISOString()
      };

      // Save interim summary
      const savedSummary = await this.saveAdminInterimSalesSummary(summary);
      
      console.log(`✅ [ADMIN INTERIM SALES] Interim sales summary generated successfully for staff ${staffId}`);
      return savedSummary;

    } catch (error) {
      console.error(`❌ [ADMIN INTERIM SALES] Failed to generate interim sales summary for staff ${staffId}:`, error);
      throw error;
    }
  }

  /**
   * Get all staff daily closing reports (from POS app's daily_closing_reports collection)
   */
  public async getAllStaffDailyClosingReports(
    dateFilter?: string,
    staffFilter?: string
  ): Promise<AdminDailyClosingReport[]> {
    try {
      console.log('📊 [ADMIN DAILY CLOSING] Fetching all staff daily closing reports from POS collection...');
      
      const { collection, getDocs, orderBy, query, where } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      // Read from POS app's daily_closing_reports collection
      let q = query(
        collection(db, 'daily_closing_reports'),
        orderBy('report_date', 'desc')
      );
      
      if (dateFilter) {
        q = query(q, where('report_date', '==', dateFilter));
      }
      
      if (staffFilter) {
        // Filter by cashier_name (staff member name) or created_by_email
        q = query(q, where('cashier_name', '==', staffFilter));
      }
      
      const querySnapshot = await getDocs(q);
      const reports: AdminDailyClosingReport[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Convert POS daily closing report format to Admin format
        const report: AdminDailyClosingReport = {
          id: doc.id,
          report_date: data.report_date || '',
          staff_member: {
            id: data.cashier_name || 'unknown',
            name: data.cashier_name || 'Unknown Staff',
            email: data.created_by_email || data.cashier_name || 'unknown@example.com',
            role: 'cashier',
            branch: data.branch || 'saheeh-riyadh'
          },
          shift_info: {
            start_time: data.shift_start || new Date(data.report_date).toISOString(),
            end_time: data.shift_end || new Date(data.report_date).toISOString(),
            duration_hours: 8, // Default
            break_time_minutes: 30
          },
          financial_performance: {
            total_sales: data.financial_summary?.total_sales || 0,
            total_orders: data.financial_summary?.total_orders || 0,
            total_customers: data.financial_summary?.total_customers || 0,
            average_order_value: data.financial_summary?.average_order_value || 0,
            gross_revenue: data.financial_summary?.gross_revenue || 0,
            net_revenue: data.financial_summary?.net_revenue || 0,
            tax_collected: data.financial_summary?.tax_collected || 0,
            discounts_given: data.financial_summary?.discounts_given || 0,
            refunds_issued: data.financial_summary?.refunds_issued || 0,
            sales_per_hour: 0,
            orders_per_hour: 0
          },
          payment_performance: data.payment_methods || {
            cash: { count: 0, amount: 0, percentage: 0 },
            card: { count: 0, amount: 0, percentage: 0 },
            paypal: { count: 0, amount: 0, percentage: 0 },
            other: { count: 0, amount: 0, percentage: 0 }
          },
          cash_management: {
            opening_cash: data.cash_reconciliation?.opening_cash || 0,
            cash_sales: data.cash_reconciliation?.cash_sales || 0,
            cash_refunds: data.cash_reconciliation?.cash_refunds || 0,
            expected_cash: data.cash_reconciliation?.expected_cash || 0,
            actual_cash: data.cash_reconciliation?.actual_cash || 0,
            cash_difference: data.cash_reconciliation?.cash_difference || 0,
            cash_over_short: data.cash_reconciliation?.cash_over_short || 0,
            cash_accuracy_percentage: 100
          },
          sales_analysis: data.sales_analysis || {
            hourly_breakdown: [],
            top_selling_items: [],
            category_performance: []
          },
          performance_metrics: data.operational_metrics ? {
            peak_performance_hour: data.operational_metrics.peak_hours?.[0] || '12:00',
            slowest_hour: data.operational_metrics.slow_hours?.[0] || '12:00',
            average_service_time_minutes: data.operational_metrics.average_service_time || 15,
            customer_satisfaction_score: data.operational_metrics.customer_satisfaction_score || 4.5,
            error_rate_percentage: 2,
            efficiency_score: 85
          } : {
            peak_performance_hour: '12:00',
            slowest_hour: '12:00',
            average_service_time_minutes: 15,
            customer_satisfaction_score: 4.5,
            error_rate_percentage: 2,
            efficiency_score: 85
          },
          team_comparison: {
            staff_rank: 1,
            total_staff_count: 1,
            above_average_performance: true,
            team_average_sales: 0,
            team_average_orders: 0,
            performance_vs_team_percentage: 0
          },
          audit_trail: data.audit_trail || {
            report_generated_at: new Date().toISOString(),
            generated_by: data.cashier_name || 'Unknown',
            last_order_time: '',
            first_order_time: '',
            system_uptime_hours: 24,
            errors_logged: 0,
            manual_adjustments: 0
          },
          status: data.status || 'draft',
          created_at: data.createdAt?.toDate?.()?.toISOString() || data.created_at || new Date().toISOString(),
          updated_at: data.updatedAt?.toDate?.()?.toISOString() || data.updated_at || new Date().toISOString()
        };
        reports.push(report);
      });

      console.log(`✅ [ADMIN DAILY CLOSING] Fetched ${reports.length} staff daily closing reports`);
      return reports;

    } catch (error: any) {
      console.error('❌ [ADMIN DAILY CLOSING] Failed to fetch staff daily closing reports:', error);
      // Return empty array instead of throwing - collection may not exist yet
      if (error?.code === 'failed-precondition' || error?.code === 'permission-denied' || error?.code === 'not-found') {
        console.warn('⚠️ [ADMIN DAILY CLOSING] Collection may not exist or needs index - returning empty');
      }
      return [];
    }
  }

  /**
   * Get staff performance summary (from actual orders and payments data)
   */
  public async getStaffPerformanceSummary(
    staffId?: string,
    periodDays: number = 30
  ): Promise<StaffPerformanceSummary[]> {
    try {
      console.log('📊 [ADMIN STAFF PERFORMANCE] Fetching staff performance summary from orders/payments...');
      
      const { collection, getDocs, query, where, orderBy } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - periodDays);
      
      // Get all orders from the period - use simple orderBy to avoid needing composite index
      const ordersRef = collection(db, 'orders');
      const ordersQuery = query(
        ordersRef,
        orderBy('created_at', 'desc')
      );

      const ordersSnapshot = await getDocs(ordersQuery);
      const orders: any[] = [];
      ordersSnapshot.forEach((doc) => {
        const data = doc.data();
        const createdAt = data.created_at?.toDate?.() || new Date(data.created_at || Date.now());
        // Filter by cutoff date in-memory to avoid composite index requirement
        if (createdAt >= cutoffDate) {
          orders.push({
            id: doc.id,
            ...data,
            created_at: createdAt.toISOString(),
            created_by_email: data.created_by_email || data.created_by || 'unknown',
            created_by_name: data.created_by_name || data.created_by || 'Unknown Staff'
          });
        }
      });
      
      // Group orders by staff member (using created_by_email)
      // Positive allow-list: only actually-billed orders count towards staff
      // performance. Excluding 'cancelled' alone was letting non-terminal
      // statuses (pending, preparing) inflate the numbers.
      const BILLED_STATUSES = ['completed', 'delivered'];
      const staffOrders: Record<string, any[]> = {};
      orders.forEach(order => {
        const status = (order.status || '').toLowerCase();
        if (BILLED_STATUSES.includes(status)) {
          const staffEmail = order.created_by_email || 'unknown';
          if (!staffOrders[staffEmail]) {
            staffOrders[staffEmail] = [];
          }
          staffOrders[staffEmail].push(order);
        }
      });
      
      // Generate performance summaries
      const summaries: StaffPerformanceSummary[] = [];
      
      for (const [staffEmail, staffOrdersList] of Object.entries(staffOrders)) {
        if (staffOrdersList.length === 0) continue;
        
        const firstOrder = staffOrdersList[0];
        const staffName = firstOrder.created_by_name || staffEmail.split('@')[0];
        
        const totalSales = staffOrdersList.reduce((sum, order) => sum + (order.total || 0), 0);
        const averageDailySales = totalSales / Math.max(periodDays, 1);
        const averageDailyOrders = staffOrdersList.length / Math.max(periodDays, 1);

        // Group orders by date to find best/worst days
        const ordersByDate: Record<string, number> = {};
        staffOrdersList.forEach(order => {
          const date = order.created_at.split('T')[0];
          ordersByDate[date] = (ordersByDate[date] || 0) + (order.total || 0);
        });
        const sortedDates = Object.entries(ordersByDate).sort((a, b) => b[1] - a[1]);
        const bestPerformanceDate = sortedDates.length > 0 ? sortedDates[0][0] : new Date().toISOString().split('T')[0];
        const worstPerformanceDate = sortedDates.length > 0 ? sortedDates[sortedDates.length - 1][0] : bestPerformanceDate;

        // Calculate consistency score from daily sales variance
        let consistencyScore = 85;
        const dailySalesValues = Object.values(ordersByDate);
        if (dailySalesValues.length > 1) {
          const mean = dailySalesValues.reduce((sum, val) => sum + val, 0) / dailySalesValues.length;
          const variance = dailySalesValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / dailySalesValues.length;
          consistencyScore = Math.max(0, Math.min(100, 100 - (Math.sqrt(variance) / (mean || 1)) * 100));
        }

        // Determine improvement trend from recent vs older orders
        let improvementTrend: 'improving' | 'declining' | 'stable' = 'stable';
        if (sortedDates.length >= 7) {
          const allDates = Object.entries(ordersByDate).sort((a, b) => a[0].localeCompare(b[0]));
          const midpoint = Math.floor(allDates.length / 2);
          const olderAvg = allDates.slice(0, midpoint).reduce((sum, [, v]) => sum + v, 0) / midpoint;
          const recentAvg = allDates.slice(midpoint).reduce((sum, [, v]) => sum + v, 0) / (allDates.length - midpoint);
          if (recentAvg > olderAvg * 1.05) improvementTrend = 'improving';
          else if (recentAvg < olderAvg * 0.95) improvementTrend = 'declining';
        }

        const totalReports = Object.keys(ordersByDate).length;
        
        const summary: StaffPerformanceSummary = {
          staff_id: staffEmail,
          staff_name: staffName,
          staff_email: staffEmail,
          staff_role: 'cashier',
          total_reports: totalReports,
          average_daily_sales: averageDailySales,
          average_daily_orders: averageDailyOrders,
          best_performance_date: bestPerformanceDate,
          worst_performance_date: worstPerformanceDate,
          consistency_score: consistencyScore,
          improvement_trend: improvementTrend,
          last_report_date: sortedDates.length > 0 ? sortedDates[0][0] : new Date().toISOString().split('T')[0],
          status: 'active'
        };
        
        summaries.push(summary);
      }
      
      // Sort by average daily sales
      summaries.sort((a, b) => b.average_daily_sales - a.average_daily_sales);
      
      console.log(`✅ [ADMIN STAFF PERFORMANCE] Generated ${summaries.length} staff performance summaries`);
      return summaries;

    } catch (error: any) {
      console.error('❌ [ADMIN STAFF PERFORMANCE] Failed to generate staff performance summary:', error);
      // Return empty array instead of throwing - index may be missing
      if (error?.code === 'failed-precondition' || error?.code === 'permission-denied') {
        console.warn('⚠️ [ADMIN STAFF PERFORMANCE] Firestore query failed (missing index?) - returning empty');
      }
      return [];
    }
  }

  /**
   * Get staff information (by email or name)
   */
  private async getStaffInfo(staffIdentifier: string): Promise<AdminDailyClosingReport['staff_member']> {
    try {
      const { collection, getDocs, query, where } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      // Try to find user by email first
      const usersRef = collection(db, 'users');
      let q = query(usersRef, where('email', '==', staffIdentifier));
      let querySnapshot = await getDocs(q);
      
      // If not found by email, try by name
      if (querySnapshot.empty) {
        q = query(usersRef, where('name', '==', staffIdentifier));
        querySnapshot = await getDocs(q);
      }
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        // Normalize role: 'staff' -> 'cashier'
        let role = (data.role || 'cashier').toLowerCase();
        if (role === 'staff') {
          role = 'cashier';
        }
        return {
          id: doc.id,
          name: data.name || data.displayName || staffIdentifier,
          email: data.email || staffIdentifier,
          role: role,
          branch: data.branch || 'saheeh-riyadh'
        };
      } else {
        // Fallback: use identifier as name/email
        return {
          id: staffIdentifier,
          name: staffIdentifier.includes('@') ? staffIdentifier.split('@')[0] : staffIdentifier,
          email: staffIdentifier.includes('@') ? staffIdentifier : `${staffIdentifier}@example.com`,
          role: 'cashier',
          branch: 'saheeh-riyadh'
        };
      }
    } catch (error) {
      console.error('❌ [ADMIN DAILY CLOSING] Failed to get staff info:', error);
      return {
        id: staffIdentifier,
        name: staffIdentifier.includes('@') ? staffIdentifier.split('@')[0] : staffIdentifier,
        email: staffIdentifier.includes('@') ? staffIdentifier : `${staffIdentifier}@example.com`,
        role: 'cashier',
        branch: 'saheeh-riyadh'
      };
    }
  }

  /**
   * Get all interim sales summaries from POS app
   */
  public async getAllInterimSalesSummaries(): Promise<AdminInterimSalesSummary[]> {
    try {
      console.log('📊 [ADMIN INTERIM SALES] Fetching interim sales summaries from POS collection...');
      
      const { collection, getDocs, query, orderBy } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      // Read from POS app's interim_sales_summaries collection
      const q = query(
        collection(db, 'interim_sales_summaries'),
        orderBy('summary_time', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const summaries: AdminInterimSalesSummary[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        summaries.push({
          id: doc.id,
          ...data,
          created_at: data.createdAt?.toDate?.()?.toISOString() || data.created_at || new Date().toISOString()
        } as AdminInterimSalesSummary);
      });

      console.log(`✅ [ADMIN INTERIM SALES] Fetched ${summaries.length} interim sales summaries`);
      return summaries;

    } catch (error) {
      console.error('❌ [ADMIN INTERIM SALES] Failed to fetch interim sales summaries:', error);
      return [];
    }
  }

  /**
   * Get recent reset events from daily_closing_resets collection
   */
  public async getRecentResetEvents(limit: number = 50): Promise<any[]> {
    try {
      console.log('📊 [ADMIN DAILY CLOSING] Fetching recent reset events...');
      
      const { collection, getDocs, query, orderBy, limit: limitQuery } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      const q = query(
        collection(db, 'daily_closing_resets'),
        orderBy('createdAt', 'desc'),
        limitQuery(limit)
      );
      
      const querySnapshot = await getDocs(q);
      const events: any[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        events.push({
          id: doc.id,
          report_id: data.report_id || '',
          report_date: data.report_date || '',
          cashier_name: data.cashier_name || '',
          branch: data.branch || '',
          previous_status: data.previous_status || 'unknown',
          performed_by_name: data.performed_by_name || '',
          performed_by_email: data.performed_by_email || '',
          reason: data.reason || '',
          created_at: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
        });
      });

      console.log(`✅ [ADMIN DAILY CLOSING] Fetched ${events.length} reset events`);
      return events;

    } catch (error) {
      console.error('❌ [ADMIN DAILY CLOSING] Failed to fetch reset events:', error);
      return [];
    }
  }

  /**
   * Generate shift information
   */
  private generateShiftInfo(shiftStart: Date, shiftEnd: Date): AdminDailyClosingReport['shift_info'] {
    const durationMs = shiftEnd.getTime() - shiftStart.getTime();
    const durationHours = durationMs / (1000 * 60 * 60);
    
    return {
      start_time: shiftStart.toISOString(),
      end_time: shiftEnd.toISOString(),
      duration_hours: durationHours,
      break_time_minutes: Math.floor(durationHours * 0.1 * 60) // 10% of shift time for breaks
    };
  }

  /**
   * Generate financial performance
   */
  private generateFinancialPerformance(
    orders: any[], 
    payments: any[], 
    shiftStart: Date, 
    shiftEnd: Date
  ): AdminDailyClosingReport['financial_performance'] {
    const completedOrders = orders.filter(order => 
      order.status === 'completed' || order.status === 'delivered'
    );
    
    const completedPayments = payments.filter(payment => 
      payment.status === 'completed'
    );

    // Use orders as source of truth for revenue (payments may be incomplete)
    // Apply correct VAT per German law:
    //   Dine-in (eat in restaurant): 19% VAT (full service)
    //   Delivery / Takeaway (Außer-Haus): 7% VAT (reduced food rate)
    let totalSales = 0;
    let netRevenue = 0;
    let taxCollected = 0;
    let netDineIn = 0;
    let netTakeaway = 0;
    let taxDineIn = 0;
    let taxTakeaway = 0;

    completedOrders.forEach(order => {
      const orderTotal = typeof order.total === 'number' ? order.total : parseFloat(order.total || '0');
      const total = isNaN(orderTotal) ? 0 : orderTotal;
      totalSales += total;

      const orderType = (order.order_type || '').toLowerCase();
      const isDineIn = orderType === 'dine_in' || orderType === 'dinein';
      const vatRate = isDineIn ? 0.19 : 0.07; // 19% dine-in, 7% takeaway/delivery

      const orderNet = total / (1 + vatRate);
      const orderTax = total - orderNet;

      netRevenue += orderNet;
      taxCollected += orderTax;
      if (isDineIn) {
        netDineIn += orderNet;
        taxDineIn += orderTax;
      } else {
        netTakeaway += orderNet;
        taxTakeaway += orderTax;
      }
    });

    netRevenue = Math.round(netRevenue * 100) / 100;
    taxCollected = Math.round(taxCollected * 100) / 100;

    const totalOrders = completedOrders.length;
    const totalCustomers = new Set(completedOrders.map(order => order.customer_name).filter(Boolean)).size;
    const averageOrderValue = totalOrders > 0 ? totalSales / totalOrders : 0;

    const shiftDurationHours = (shiftEnd.getTime() - shiftStart.getTime()) / (1000 * 60 * 60);
    const salesPerHour = shiftDurationHours > 0 ? totalSales / shiftDurationHours : 0;
    const ordersPerHour = shiftDurationHours > 0 ? totalOrders / shiftDurationHours : 0;

    const discountsGiven = completedOrders.reduce((sum, order) => sum + (order.discount_amount || 0), 0);
    const refundsIssued = payments.reduce((sum, payment) => sum + (payment.refund_amount || 0), 0);

    console.log(`[CLOSING REPORT] VAT breakdown — Dine-in: net €${netDineIn.toFixed(2)} / tax €${taxDineIn.toFixed(2)} (19%), Takeaway: net €${netTakeaway.toFixed(2)} / tax €${taxTakeaway.toFixed(2)} (7%)`);

    return {
      total_sales: totalSales,
      total_orders: totalOrders,
      total_customers: totalCustomers,
      average_order_value: averageOrderValue,
      gross_revenue: totalSales,
      net_revenue: netRevenue,
      tax_collected: taxCollected,
      discounts_given: discountsGiven,
      refunds_issued: refundsIssued,
      sales_per_hour: salesPerHour,
      orders_per_hour: ordersPerHour
    };
  }

  /**
   * Generate payment performance
   */
  private generatePaymentPerformance(payments: any[]): AdminDailyClosingReport['payment_performance'] {
    const completedPayments = payments.filter(payment => payment.status === 'completed');
    
    const methods: Record<string, { count: number; amount: number }> = {
      cash: { count: 0, amount: 0 },
      card: { count: 0, amount: 0 },
      paypal: { count: 0, amount: 0 },
      other: { count: 0, amount: 0 }
    };

    completedPayments.forEach(payment => {
      const method = payment.payment_method?.toLowerCase() || 'other';
      const amount = payment.amount || 0;
      
      if (methods[method]) {
        methods[method].count++;
        methods[method].amount += amount;
      } else {
        methods.other.count++;
        methods.other.amount += amount;
      }
    });

    const totalAmount = Object.values(methods).reduce((sum, method) => sum + method.amount, 0);

    return {
      cash: {
        ...methods.cash,
        percentage: totalAmount > 0 ? (methods.cash.amount / totalAmount) * 100 : 0
      },
      card: {
        ...methods.card,
        percentage: totalAmount > 0 ? (methods.card.amount / totalAmount) * 100 : 0
      },
      paypal: {
        ...methods.paypal,
        percentage: totalAmount > 0 ? (methods.paypal.amount / totalAmount) * 100 : 0
      },
      other: {
        ...methods.other,
        percentage: totalAmount > 0 ? (methods.other.amount / totalAmount) * 100 : 0
      }
    };
  }

  /**
   * Generate cash management
   */
  private generateCashManagement(
    orders: any[], 
    payments: any[], 
    openingCash: number, 
    actualCash: number
  ): AdminDailyClosingReport['cash_management'] {
    const cashPayments = payments.filter(payment => 
      payment.payment_method?.toLowerCase() === 'cash' && payment.status === 'completed'
    );
    
    const cashRefunds = payments.filter(payment => 
      payment.payment_method?.toLowerCase() === 'cash' && payment.refund_amount > 0
    );

    const cashSales = cashPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    const cashRefundAmount = cashRefunds.reduce((sum, payment) => sum + (payment.refund_amount || 0), 0);
    
    const expectedCash = openingCash + cashSales - cashRefundAmount;
    const cashDifference = actualCash - expectedCash;
    const cashAccuracyPercentage = expectedCash > 0 ? (1 - Math.abs(cashDifference) / expectedCash) * 100 : 100;

    return {
      opening_cash: openingCash,
      cash_sales: cashSales,
      cash_refunds: cashRefundAmount,
      expected_cash: expectedCash,
      actual_cash: actualCash,
      cash_difference: cashDifference,
      cash_over_short: Math.abs(cashDifference),
      cash_accuracy_percentage: cashAccuracyPercentage
    };
  }

  /**
   * Generate sales analysis
   */
  private generateSalesAnalysis(orders: any[]): AdminDailyClosingReport['sales_analysis'] {
    const completedOrders = orders.filter(order => 
      order.status === 'completed' || order.status === 'delivered'
    );

    // Hourly breakdown
    const hourlyData: Record<number, { orders: number; revenue: number }> = {};
    for (let hour = 0; hour < 24; hour++) {
      hourlyData[hour] = { orders: 0, revenue: 0 };
    }

    completedOrders.forEach(order => {
      const orderDate = new Date(order.created_at);
      const hour = orderDate.getHours();
      hourlyData[hour].orders++;
      hourlyData[hour].revenue += order.total || 0;
    });

    const hourlyBreakdown = Object.entries(hourlyData).map(([hour, data]) => ({
      hour: parseInt(hour),
      orders: data.orders,
      revenue: data.revenue,
      average_order_value: data.orders > 0 ? data.revenue / data.orders : 0
    }));

    // Top selling items
    const itemCounts: Record<string, { quantity: number; revenue: number }> = {};
    completedOrders.forEach(order => {
      if (order.items) {
        order.items.forEach((item: any) => {
          const itemName = item.menu_item_name || item.name || 'Unknown Item';
          if (!itemCounts[itemName]) {
            itemCounts[itemName] = { quantity: 0, revenue: 0 };
          }
          itemCounts[itemName].quantity += item.quantity || 0;
          itemCounts[itemName].revenue += item.total || 0;
        });
      }
    });

    const totalRevenue = completedOrders.reduce((sum, order) => sum + (order.total || 0), 0);
    const topSellingItems = Object.entries(itemCounts)
      .map(([name, data]) => ({
        name,
        quantity: data.quantity,
        revenue: data.revenue,
        percentage_of_sales: totalRevenue > 0 ? (data.revenue / totalRevenue) * 100 : 0
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);

    // Category performance (simplified)
    const categoryPerformance = [
      { category: 'Sushi', orders: Math.floor(completedOrders.length * 0.4), revenue: totalRevenue * 0.4, percentage_of_sales: 40 },
      { category: 'Pizza', orders: Math.floor(completedOrders.length * 0.3), revenue: totalRevenue * 0.3, percentage_of_sales: 30 },
      { category: 'Pasta', orders: Math.floor(completedOrders.length * 0.2), revenue: totalRevenue * 0.2, percentage_of_sales: 20 },
      { category: 'Other', orders: Math.floor(completedOrders.length * 0.1), revenue: totalRevenue * 0.1, percentage_of_sales: 10 }
    ];

    return {
      hourly_breakdown: hourlyBreakdown,
      top_selling_items: topSellingItems,
      category_performance: categoryPerformance
    };
  }

  /**
   * Generate performance metrics
   */
  private generatePerformanceMetrics(orders: any[]): AdminDailyClosingReport['performance_metrics'] {
    const completedOrders = orders.filter(order => 
      order.status === 'completed' || order.status === 'delivered'
    );

    // Find peak and slow hours
    const hourlyOrders: Record<number, number> = {};
    completedOrders.forEach(order => {
      const hour = new Date(order.created_at).getHours();
      hourlyOrders[hour] = (hourlyOrders[hour] || 0) + 1;
    });

    const sortedHours = Object.entries(hourlyOrders)
      .sort(([,a], [,b]) => b - a)
      .map(([hour]) => `${hour}:00`);

    const peakPerformanceHour = sortedHours[0] || '12:00';
    const slowestHour = sortedHours[sortedHours.length - 1] || '12:00';

    // Calculate average service time (simplified)
    const averageServiceTime = completedOrders.length > 0 ? 15 : 0; // 15 minutes average

    // Calculate error rate (simplified)
    const errorRate = completedOrders.length > 0 ? 2 : 0; // 2% error rate

    // Calculate efficiency score
    const efficiencyScore = Math.min(100, Math.max(0, 
      (completedOrders.length * 10) + 
      (completedOrders.reduce((sum, order) => sum + (order.total || 0), 0) / 100) +
      (100 - errorRate)
    ));

    return {
      peak_performance_hour: peakPerformanceHour,
      slowest_hour: slowestHour,
      average_service_time_minutes: averageServiceTime,
      customer_satisfaction_score: 4.5, // This would need feedback system
      error_rate_percentage: errorRate,
      efficiency_score: efficiencyScore
    };
  }

  /**
   * Generate team comparison
   */
  private async generateTeamComparison(
    staffEmail: string,
    reportDate: string,
    orders: any[],
    payments: any[]
  ): Promise<AdminDailyClosingReport['team_comparison']> {
    try {
      // Get all staff reports for the same date
      const allReports = await this.getAllStaffDailyClosingReports(reportDate);
      
      const staffSales = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
      const staffOrders = orders.length;
      
      // Calculate team averages
      const teamAverageSales = allReports.reduce((sum, report) => 
        sum + report.financial_performance.total_sales, 0
      ) / Math.max(allReports.length, 1);
      
      const teamAverageOrders = allReports.reduce((sum, report) => 
        sum + report.financial_performance.total_orders, 0
      ) / Math.max(allReports.length, 1);
      
      // Find staff rank
      const sortedBySales = [...allReports].sort((a, b) => 
        b.financial_performance.total_sales - a.financial_performance.total_sales
      );
      
      const staffRank = sortedBySales.findIndex(report => 
        report.staff_member.email === staffEmail || report.staff_member.name === staffEmail
      ) + 1;
      
      const performanceVsTeam = teamAverageSales > 0 ? 
        ((staffSales - teamAverageSales) / teamAverageSales) * 100 : 0;

      return {
        staff_rank: staffRank || 1,
        total_staff_count: allReports.length,
        above_average_performance: staffSales > teamAverageSales,
        team_average_sales: teamAverageSales,
        team_average_orders: teamAverageOrders,
        performance_vs_team_percentage: performanceVsTeam
      };
    } catch (error) {
      console.error('❌ [ADMIN DAILY CLOSING] Failed to generate team comparison:', error);
      return {
        staff_rank: 1,
        total_staff_count: 1,
        above_average_performance: true,
        team_average_sales: 0,
        team_average_orders: 0,
        performance_vs_team_percentage: 0
      };
    }
  }

  /**
   * Generate audit trail
   */
  private generateAuditTrail(orders: any[]): AdminDailyClosingReport['audit_trail'] {
    const orderTimes = orders.map(order => new Date(order.created_at));
    const firstOrderTime = orderTimes.length > 0 ? Math.min(...orderTimes.map(t => t.getTime())) : 0;
    const lastOrderTime = orderTimes.length > 0 ? Math.max(...orderTimes.map(t => t.getTime())) : 0;

    return {
      report_generated_at: new Date().toISOString(),
      generated_by: 'Admin System',
      last_order_time: lastOrderTime > 0 ? new Date(lastOrderTime).toISOString() : '',
      first_order_time: firstOrderTime > 0 ? new Date(firstOrderTime).toISOString() : '',
      system_uptime_hours: 24, // 24 hours
      errors_logged: 0,
      manual_adjustments: 0
    };
  }

  /**
   * Generate current performance for interim summary
   */
  private generateCurrentPerformance(
    orders: any[], 
    payments: any[], 
    periodHours: number
  ): AdminInterimSalesSummary['current_performance'] {
    const completedOrders = orders.filter(order => 
      order.status === 'completed' || order.status === 'delivered'
    );
    
    const completedPayments = payments.filter(payment => payment.status === 'completed');
    
    const currentSales = completedPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    const currentOrders = completedOrders.length;
    const hourlyRate = periodHours > 0 ? currentSales / periodHours : 0;
    
    // Project daily sales based on current rate
    const projectedDailySales = hourlyRate * 12; // Assuming 12-hour operation
    const projectedDailyOrders = (currentOrders / periodHours) * 12;
    
    // Calculate time to reach daily goal (assuming €1000 goal)
    const dailyGoal = 1000;
    const timeToGoal = hourlyRate > 0 ? `${Math.ceil((dailyGoal - currentSales) / hourlyRate)} hours` : 'N/A';
    const goalCompletionPercentage = (currentSales / dailyGoal) * 100;

    return {
      current_sales: currentSales,
      current_orders: currentOrders,
      hourly_rate: hourlyRate,
      projected_daily_sales: projectedDailySales,
      projected_daily_orders: projectedDailyOrders,
      time_to_goal: timeToGoal,
      goal_completion_percentage: goalCompletionPercentage
    };
  }

  /**
   * Generate recent activity for interim summary
   */
  private generateRecentActivity(
    orders: any[], 
    payments: any[]
  ): AdminInterimSalesSummary['recent_activity'] {
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - (60 * 60 * 1000));
    
    const recentOrders = orders.filter(order => 
      new Date(order.created_at) >= oneHourAgo && 
      (order.status === 'completed' || order.status === 'delivered')
    );
    
    const recentPayments = payments.filter(payment => 
      new Date(payment.created_at) >= oneHourAgo && payment.status === 'completed'
    );

    const lastHourOrders = recentOrders.length;
    const lastHourRevenue = recentPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    const lastHourCustomers = new Set(recentOrders.map(order => order.customer_name)).size;
    
    // Find peak performance hour
    const hourlyData: Record<number, number> = {};
    recentOrders.forEach(order => {
      const hour = new Date(order.created_at).getHours();
      hourlyData[hour] = (hourlyData[hour] || 0) + 1;
    });
    
    const peakHour = Object.entries(hourlyData)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || '12';
    
    const peakPerformanceHour = `${peakHour}:00`;

    // Calculate trend
    const trendPercentage = lastHourOrders > 5 ? 15 : lastHourOrders < 2 ? -10 : 0;
    const currentTrend = lastHourOrders > 5 ? 'increasing' : lastHourOrders < 2 ? 'decreasing' : 'stable';

    return {
      last_hour_orders: lastHourOrders,
      last_hour_revenue: lastHourRevenue,
      last_hour_customers: lastHourCustomers,
      peak_performance_hour: peakPerformanceHour,
      current_trend: currentTrend,
      trend_percentage: trendPercentage
    };
  }

  /**
   * Generate performance alerts for interim summary
   */
  private generatePerformanceAlerts(
    orders: any[], 
    payments: any[]
  ): AdminInterimSalesSummary['performance_alerts'] {
    const alerts: AdminInterimSalesSummary['performance_alerts'] = [];
    const now = new Date();
    
    // Check for low sales alert
    const recentOrders = orders.filter(order => 
      new Date(order.created_at) >= new Date(now.getTime() - (2 * 60 * 60 * 1000)) &&
      (order.status === 'completed' || order.status === 'delivered')
    );
    
    if (recentOrders.length < 3) {
      alerts.push({
        type: 'warning',
        message: 'Low sales activity in the last 2 hours',
        timestamp: now.toISOString(),
        severity: 'medium'
      });
    }
    
    // Check for high sales alert
    if (recentOrders.length > 15) {
      alerts.push({
        type: 'achievement',
        message: 'Excellent sales performance!',
        timestamp: now.toISOString(),
        severity: 'low'
      });
    }
    
    // Check for payment issues
    const failedPayments = payments.filter(payment => payment.status === 'failed');
    if (failedPayments.length > 0) {
      alerts.push({
        type: 'error',
        message: `${failedPayments.length} failed payment(s) detected`,
        timestamp: now.toISOString(),
        severity: 'high'
      });
    }

    return alerts;
  }

  /**
   * Generate staff comparison for interim summary
   */
  private async generateStaffComparison(
    staffEmail: string,
    orders: any[],
    payments: any[]
  ): Promise<AdminInterimSalesSummary['staff_comparison']> {
    try {
      // Get all active staff from performance summary
      const allStaff = await this.getStaffPerformanceSummary();
      const staffSales = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
      
      // Calculate rank among active staff
      const sortedStaff = allStaff.sort((a, b) => b.average_daily_sales - a.average_daily_sales);
      const rank = sortedStaff.findIndex(staff => staff.staff_email === staffEmail || staff.staff_id === staffEmail) + 1;
      
      // Calculate performance vs peers
      const averagePeerSales = allStaff.reduce((sum, staff) => sum + staff.average_daily_sales, 0) / Math.max(allStaff.length, 1);
      const performanceVsPeers = averagePeerSales > 0 ? 
        ((staffSales - averagePeerSales) / averagePeerSales) * 100 : 0;
      
      // Find best performing staff
      const bestPerformingStaff = sortedStaff[0]?.staff_name || 'N/A';
      
      // Areas for improvement (simplified)
      const areasForImprovement: string[] = [];
      if (staffSales < averagePeerSales * 0.8) {
        areasForImprovement.push('Sales volume');
      }
      if (orders.length < 5) {
        areasForImprovement.push('Order processing speed');
      }

      return {
        rank_among_staff: rank || 1,
        total_active_staff: allStaff.length,
        performance_vs_peers: performanceVsPeers,
        best_performing_staff: bestPerformingStaff,
        areas_for_improvement: areasForImprovement
      };
    } catch (error) {
      console.error('❌ [ADMIN INTERIM SALES] Failed to generate staff comparison:', error);
      return {
        rank_among_staff: 1,
        total_active_staff: 1,
        performance_vs_peers: 0,
        best_performing_staff: 'N/A',
        areas_for_improvement: []
      };
    }
  }

  /**
   * Get all active staff
   */
  private async getAllActiveStaff(): Promise<Array<{ id: string; name: string; currentSales: number }>> {
    try {
      const { collection, getDocs } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      const usersRef = collection(db, 'users');
      const querySnapshot = await getDocs(usersRef);
      
      const activeStaff: Array<{ id: string; name: string; currentSales: number }> = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Normalize role: 'staff' -> 'cashier'
        const role = (data.role || 'cashier').toLowerCase();
        const normalizedRole = role === 'staff' ? 'cashier' : role;
        if (normalizedRole === 'cashier' || normalizedRole === 'manager') {
          activeStaff.push({
            id: doc.id,
            name: data.name || data.displayName || 'Unknown Staff',
            currentSales: Math.random() * 1000 // This would be calculated from real data
          });
        }
      });
      
      return activeStaff;
    } catch (error) {
      console.error('❌ [ADMIN INTERIM SALES] Failed to get active staff:', error);
      return [];
    }
  }

  /**
   * Get staff orders for specific date (using created_by_email)
   */
  private async getStaffOrdersForDate(staffEmail: string, date: string): Promise<any[]> {
    try {
      const { collection, getDocs, query, where, orderBy } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      // Parse date string "YYYY-MM-DD" as LOCAL date (not UTC)
      const [_y, _m, _d] = date.split('-').map(Number);
      const startOfDay = new Date(_y, _m - 1, _d, 0, 0, 0, 0);
      const endOfDay = new Date(_y, _m - 1, _d, 23, 59, 59, 999);
      
      const ordersRef = collection(db, 'orders');
      // Query by created_by_email or created_by (fallback)
      const q = query(
        ordersRef,
        where('created_at', '>=', startOfDay),
        where('created_at', '<=', endOfDay),
        orderBy('created_at', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const orders: any[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const orderEmail = data.created_by_email || data.created_by || '';
        // Filter by staff email
        if (orderEmail === staffEmail || orderEmail.includes(staffEmail) || staffEmail.includes(orderEmail)) {
          orders.push({
            id: doc.id,
            ...data,
            created_at: data.created_at?.toDate?.()?.toISOString() || data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
          });
        }
      });
      
      return orders;
    } catch (error) {
      console.error('❌ [ADMIN DAILY CLOSING] Failed to fetch staff orders for date:', error);
      return [];
    }
  }

  /**
   * Get staff payments for specific date (using processed_by or order_id lookup)
   */
  private async getStaffPaymentsForDate(staffEmail: string, date: string): Promise<any[]> {
    try {
      const { collection, getDocs, query, where, orderBy } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      // Parse date string "YYYY-MM-DD" as LOCAL date (not UTC)
      const [_y, _m, _d] = date.split('-').map(Number);
      const startOfDay = new Date(_y, _m - 1, _d, 0, 0, 0, 0);
      const endOfDay = new Date(_y, _m - 1, _d, 23, 59, 59, 999);
      
      // First get orders for this staff member on this date
      const staffOrders = await this.getStaffOrdersForDate(staffEmail, date);
      const orderIds = staffOrders.map(o => o.id);
      
      const paymentsRef = collection(db, 'payments');
      const q = query(
        paymentsRef,
        where('created_at', '>=', startOfDay),
        where('created_at', '<=', endOfDay),
        orderBy('created_at', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const payments: any[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Match payments to orders created by this staff member
        const paymentOrderId = data.order_id || '';
        if (orderIds.includes(paymentOrderId) || data.processed_by === staffEmail) {
          payments.push({
            id: doc.id,
            ...data,
            created_at: data.created_at?.toDate?.()?.toISOString() || data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
          });
        }
      });
      
      return payments;
    } catch (error) {
      console.error('❌ [ADMIN DAILY CLOSING] Failed to fetch staff payments for date:', error);
      return [];
    }
  }

  /**
   * Get staff orders for period (using created_by_email)
   */
  private async getStaffOrdersForPeriod(staffEmail: string, startDate: string, endDate: string): Promise<any[]> {
    try {
      const { collection, getDocs, query, where, orderBy } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      const ordersRef = collection(db, 'orders');
      const q = query(
        ordersRef,
        where('created_at', '>=', new Date(startDate)),
        where('created_at', '<=', new Date(endDate)),
        orderBy('created_at', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const orders: any[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const orderEmail = data.created_by_email || data.created_by || '';
        // Filter by staff email
        if (orderEmail === staffEmail || orderEmail.includes(staffEmail) || staffEmail.includes(orderEmail)) {
          orders.push({
            id: doc.id,
            ...data,
            created_at: data.created_at?.toDate?.()?.toISOString() || data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
          });
        }
      });
      
      return orders;
    } catch (error) {
      console.error('❌ [ADMIN DAILY CLOSING] Failed to fetch staff orders for period:', error);
      return [];
    }
  }

  /**
   * Get staff payments for period (using processed_by or order_id lookup)
   */
  private async getStaffPaymentsForPeriod(staffEmail: string, startDate: string, endDate: string): Promise<any[]> {
    try {
      // First get orders for this staff member in this period
      const staffOrders = await this.getStaffOrdersForPeriod(staffEmail, startDate, endDate);
      const orderIds = staffOrders.map(o => o.id);
      
      const { collection, getDocs, query, where, orderBy } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      const paymentsRef = collection(db, 'payments');
      const q = query(
        paymentsRef,
        where('created_at', '>=', new Date(startDate)),
        where('created_at', '<=', new Date(endDate)),
        orderBy('created_at', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const payments: any[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Match payments to orders created by this staff member
        const paymentOrderId = data.order_id || '';
        if (orderIds.includes(paymentOrderId) || data.processed_by === staffEmail) {
          payments.push({
            id: doc.id,
            ...data,
            created_at: data.created_at?.toDate?.()?.toISOString() || data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
          });
        }
      });
      
      return payments;
    } catch (error) {
      console.error('❌ [ADMIN DAILY CLOSING] Failed to fetch staff payments for period:', error);
      return [];
    }
  }

  /**
   * Save admin daily closing report to Firebase
   */
  private async saveAdminDailyClosingReport(report: Omit<AdminDailyClosingReport, 'id'>): Promise<AdminDailyClosingReport> {
    try {
      const { collection, addDoc, serverTimestamp } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      const docRef = await addDoc(collection(db, 'admin_daily_closing_reports'), {
        ...report,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      const savedReport: AdminDailyClosingReport = {
        ...report,
        id: docRef.id
      };

      console.log('✅ [ADMIN DAILY CLOSING] Admin daily closing report saved successfully');
      return savedReport;

    } catch (error) {
      console.error('❌ [ADMIN DAILY CLOSING] Failed to save admin daily closing report:', error);
      throw error;
    }
  }

  /**
   * Save admin interim sales summary to Firebase
   */
  private async saveAdminInterimSalesSummary(summary: Omit<AdminInterimSalesSummary, 'id'>): Promise<AdminInterimSalesSummary> {
    try {
      const { collection, addDoc, serverTimestamp } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      const docRef = await addDoc(collection(db, 'admin_interim_sales_summaries'), {
        ...summary,
        createdAt: serverTimestamp()
      });

      const savedSummary: AdminInterimSalesSummary = {
        ...summary,
        id: docRef.id
      };

      console.log('✅ [ADMIN INTERIM SALES] Admin interim sales summary saved successfully');
      return savedSummary;

    } catch (error) {
      console.error('❌ [ADMIN INTERIM SALES] Failed to save admin interim sales summary:', error);
      throw error;
    }
  }
}

export default AdminDailyClosingReportsService;
